<template>
  <app-layout>
    <section class="my-lists-layout">
      <section
        ref="sidebar"
        class="sidebar"
        :class="{
          'sidebar--collapsed': is_sidebar_collapsed,
          'sidebar--collapsed-settled': is_sidebar_collapsed && is_sidebar_collapsed_settled,
        }"
        @transitionend="onSidebarTransitionEnd($event)"
      >
        <div class="sidebar-menu">
          <div class="sidebar-toggle">
            <button-element
              color="blue-o"
              :icon="is_sidebar_collapsed ? 'chevron-right' : 'chevron-left'"
              width="100%"
              :aria-label="is_sidebar_collapsed ? $t('expand_sidebar') : $t('collapse_sidebar')"
              :title="is_sidebar_collapsed ? $t('expand_sidebar') : $t('collapse_sidebar')"
              @click="toggleSidebar()"
            />
          </div>

          <my-lists-sidebar-element
            :collapsed="is_sidebar_collapsed"
          />
        </div>
      </section>

      <section class="main">
        <slot />
      </section>
    </section>
  </app-layout>
</template>

<script>
import AppLayout from '@/components/layouts/AppLayout.vue';
import ButtonElement from '@/components/elements/ButtonElement.vue';
import MyListsSidebarElement from '@/components/elements/my-lists/MyListsSidebarElement.vue';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'my_lists_sidebar_collapsed';
const SIDEBAR_TRANSITION_DURATION = 360;

export default {
  name: 'MyListsLayout',
  components: {
    AppLayout,
    ButtonElement,
    MyListsSidebarElement,
  },
  data: () => ({
    is_sidebar_collapsed: false,
    is_sidebar_collapsed_settled: false,
    sidebar_settled_timeout: null,
  }),
  mounted() {
    this.is_sidebar_collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    this.is_sidebar_collapsed_settled = this.is_sidebar_collapsed;
  },
  beforeUnmount() {
    window.clearTimeout(this.sidebar_settled_timeout);
  },
  methods: {
    toggleSidebar() {
      this.is_sidebar_collapsed_settled = false;
      this.is_sidebar_collapsed = !this.is_sidebar_collapsed;
      localStorage.setItem(
        SIDEBAR_COLLAPSED_STORAGE_KEY,
        this.is_sidebar_collapsed ? 'true' : 'false'
      );
      this.scheduleSidebarSettledState();
    },
    onSidebarTransitionEnd(event) {
      if (event.target !== this.$refs.sidebar || event.propertyName !== 'width') {
        return;
      }

      window.clearTimeout(this.sidebar_settled_timeout);
      this.is_sidebar_collapsed_settled = this.is_sidebar_collapsed;
    },
    scheduleSidebarSettledState() {
      window.clearTimeout(this.sidebar_settled_timeout);

      if (!this.is_sidebar_collapsed) {
        return;
      }

      this.sidebar_settled_timeout = window.setTimeout(() => {
        if (this.is_sidebar_collapsed) {
          this.is_sidebar_collapsed_settled = true;
        }
      }, SIDEBAR_TRANSITION_DURATION);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

$sidebar-width-expanded: 272px;
$sidebar-width-collapsed: 64px;
$sidebar-transition: 0.35s cubic-bezier(0.4, 0, 0.2, 1);

.my-lists-layout {
  background-color: $white;
  display: flex;
  font-size: 12px;
  height: calc(100vh - 100px);
  max-height: calc(100vh - 100px);
  overflow: hidden;
}

.sidebar {
  background-color: $white;
  border-right: 1px solid $medium-gray;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  min-width: $sidebar-width-expanded;
  overflow: hidden;
  padding: 24px 16px;
  transition: width $sidebar-transition, min-width $sidebar-transition;
  width: $sidebar-width-expanded;

  &--collapsed {
    min-width: $sidebar-width-collapsed;
    width: $sidebar-width-collapsed;
    z-index: map-get($zindex, sequence-v2-template-menu);
  }

  &--collapsed-settled {
    .sidebar-menu {
      align-items: center;
    }
  }
}

.sidebar-toggle {
  align-self: flex-start;
  flex-shrink: 0;
  margin-bottom: 16px;
  width: 100%;

  .sidebar--collapsed & {
    align-self: center;
    width: 32px;

    :deep(.button) {
      height: 32px;
      min-width: 0;
      padding: 0;
      width: 32px;
    }
  }

  :deep(.button) {
    background-color: $zumthor;
    border: none;
    border-radius: 8px;
    width: 100%;

    svg {
      --color: #{$cornflower-blue};
    }

    &:not(:disabled):hover {
      background-color: $zumthor;
      border: none;

      svg {
        --color: #{$medium-slate-blue};
      }
    }
  }
}

.sidebar-menu {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
}

.main {
  background-color: $white;
  flex: 1;
  height: 100%;
  min-width: 300px;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>

<i18n src="@/locales/components/layouts/my-lists-layout.json"></i18n>
